import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, KeyRound, Pencil, Search, ShieldCheck, UserMinus, UserPlus, UserRoundCheck } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { EditorModal } from "@/components/EditorModal";
import { ReassignTasksPanel } from "@/components/ReassignTasksPanel";
import { TeamCredentialResult } from "@/components/TeamCredentialResult";
import { EmailDeliveryStatus } from "@/components/EmailDeliveryStatus";
import { WeeklyDigestCard } from "@/components/WeeklyDigestCard";
import { TaskRoutingCard } from "@/components/TaskRoutingCard";
import { TeamMemberForm, roleLabel } from "@/components/TeamMemberForm";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const emptyForm = { first_name: "", last_name: "", email: "", role: "operations", title: "" };

function formatDate(value) {
  if (!value) {
    return "Never";
  }
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function accessState(member) {
  if (!member.active) {
    return { className: "access-badge access-inactive", label: "Deactivated" };
  }
  if (member.password_change_required) {
    return { className: "access-badge access-pending", label: "Temp password pending" };
  }
  return { className: "access-badge access-active", label: "Active" };
}

function ConfirmPanel({ confirmLabel, description, onCancel, onConfirm, testId, title }) {
  return (
    <section className="inline-edit-panel team-member-panel" data-testid={testId}>
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      <div className="team-confirm-actions">
        <button className="button button-secondary" data-testid={`${testId}-cancel`} type="button" onClick={onCancel}>
          Cancel
        </button>
        <button className="button button-primary" data-testid={`${testId}-confirm`} type="button" onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </section>
  );
}

export default function TeamDirectoryPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [credential, setCredential] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [reassigning, setReassigning] = useState(null);
  const canReassign = ["system_administrator", "hr"].includes(user?.role);

  async function load() {
    try {
      const response = await api.get("/team/members");
      setMembers(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(
    () => ({
      total: members.length,
      active: members.filter((member) => member.active).length,
      pending: members.filter((member) => member.active && member.password_change_required).length,
      admins: members.filter((member) => member.active && member.role === "system_administrator").length,
    }),
    [members],
  );

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      return members;
    }
    return members.filter((member) =>
      [member.first_name, member.last_name, member.email, member.title, roleLabel(member.role)]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [members, query]);

  function openCreate() {
    setForm(emptyForm);
    setEditor({ mode: "create" });
    setError("");
  }

  function openEdit(member) {
    setForm({
      first_name: member.first_name,
      last_name: member.last_name,
      email: member.email,
      role: member.role,
      title: member.title || "",
    });
    setEditor({ mode: "edit", member });
    setError("");
  }

  function replaceMember(updated) {
    setMembers((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  }

  async function submitForm(event) {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    try {
      if (editor.mode === "create") {
        const response = await api.post("/team/members", form);
        setMembers((current) => [response.data.member, ...current]);
        setEditor(null);
        setCredential({ mode: "create", ...response.data });
      } else {
        const { email, ...updates } = form;
        const response = await api.patch(`/team/members/${editor.member.id}`, updates);
        replaceMember(response.data);
        setEditor(null);
        setMessage(`${response.data.first_name} ${response.data.last_name} updated.`);
      }
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  async function runConfirm() {
    const { action, member } = confirm;
    setConfirm(null);
    setError("");
    try {
      if (action === "reset") {
        const response = await api.post(`/team/members/${member.id}/reset-password`);
        replaceMember(response.data.member);
        setCredential({ mode: "reset", ...response.data });
      } else {
        const response = await api.patch(`/team/members/${member.id}`, { active: action === "reactivate" });
        replaceMember(response.data);
        setMessage(
          `${response.data.first_name} ${response.data.last_name} ${action === "reactivate" ? "reactivated — they can sign in again" : "deactivated — all sessions ended and sign-in is blocked"}.`,
        );
        if (action === "deactivate" && ["system_administrator", "hr", "operations"].includes(member.role)) {
          setReassigning(member);
        }
      }
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  const confirmCopy = {
    reset: {
      title: "Reset password?",
      description: "A new temporary password will replace the current one immediately. Their active sessions stay signed in until the next refresh.",
      confirmLabel: "Issue temporary password",
    },
    deactivate: {
      title: "Deactivate account?",
      description: "They will be signed out on their next request and cannot sign in until reactivated. Their records stay intact.",
      confirmLabel: "Deactivate",
    },
    reactivate: {
      title: "Reactivate account?",
      description: "Their previous password stays in place. Use Reset password afterwards if they no longer know it.",
      confirmLabel: "Reactivate",
    },
  };

  return (
    <div className="app-shell" data-testid="team-directory-shell">
      <AppSidebar activeItem="Team Directory" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page" data-testid="team-directory-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">TAG ADMINISTRATION</p>
              <h1>Team Directory</h1>
              <p className="page-subtitle">
                Add internal staff, set their role, and issue or reset sign-in credentials without leaving TAGForce.
              </p>
            </div>
            <button className="button button-primary" data-testid="add-team-member-button" type="button" onClick={openCreate}>
              <UserPlus size={16} /> Add staff member
            </button>
          </div>

          <EmailDeliveryStatus />
          <WeeklyDigestCard />
          <TaskRoutingCard canEdit={user?.role === "system_administrator"} onSaved={setMessage} />

          <section className="report-metric-grid team-metric-grid" data-testid="team-metrics">
            <article>
              <small>TEAM MEMBERS</small>
              <strong data-testid="team-metric-total">{loaded ? stats.total : "—"}</strong>
            </article>
            <article>
              <small>ACTIVE ACCOUNTS</small>
              <strong data-testid="team-metric-active">{loaded ? stats.active : "—"}</strong>
            </article>
            <article>
              <small>AWAITING FIRST SIGN-IN</small>
              <strong data-testid="team-metric-pending">{loaded ? stats.pending : "—"}</strong>
            </article>
            <article>
              <small>ADMINISTRATORS</small>
              <strong data-testid="team-metric-admins">{loaded ? stats.admins : "—"}</strong>
            </article>
          </section>

          {error && (
            <p className="form-message error-message" data-testid="team-directory-error">
              {error}
            </p>
          )}
          {message && (
            <p className="form-message success-message" data-testid="team-directory-message">
              {message}
            </p>
          )}

          <section className="requests-table-panel" data-testid="team-members-table">
            <div className="section-heading team-toolbar">
              <div>
                <h2>Internal staff</h2>
                <p data-testid="team-count">{visible.length} of {members.length} accounts shown</p>
              </div>
              <label className="team-search">
                <Search size={14} />
                <input
                  data-testid="team-search-input"
                  placeholder="Search name, email, role…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            </div>
            <div className="request-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Access</th>
                    <th>Last sign-in</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((member) => {
                    const access = accessState(member);
                    const isSelf = member.id === user.id;
                    return (
                      <tr key={member.id} className={member.active ? "" : "team-row-inactive"} data-testid={`team-row-${member.id}`}>
                        <td>
                          <span className="team-member-cell">
                            <span className="team-avatar" aria-hidden="true">
                              {member.first_name[0]}
                              {member.last_name?.[0] || ""}
                            </span>
                            <span>
                              <strong>
                                {member.first_name} {member.last_name}
                                {isSelf && <em className="team-you-tag">You</em>}
                              </strong>
                              {member.title && <small>{member.title}</small>}
                            </span>
                          </span>
                        </td>
                        <td>{member.email}</td>
                        <td>
                          <span className={`role-badge role-${member.role}`}>
                            {member.role === "system_administrator" && <ShieldCheck size={11} />} {roleLabel(member.role)}
                          </span>
                        </td>
                        <td>
                          <span className={access.className} data-testid={`team-access-${member.id}`}>{access.label}</span>
                        </td>
                        <td>{formatDate(member.last_login_at)}</td>
                        <td>
                          <div className="table-action-stack team-actions">
                            <button className="table-edit-button" data-testid={`edit-team-member-${member.id}`} type="button" onClick={() => openEdit(member)}>
                              <Pencil size={13} /> Edit
                            </button>
                            {member.active && (
                              <button className="table-edit-button" data-testid={`reset-team-password-${member.id}`} type="button" onClick={() => setConfirm({ action: "reset", member })}>
                                <KeyRound size={13} /> Reset password
                              </button>
                            )}
                            {canReassign && ["system_administrator", "hr", "operations"].includes(member.role) && (
                              <button className="table-edit-button" data-testid={`reassign-tasks-${member.id}`} type="button" onClick={() => setReassigning(member)}>
                                <ArrowRightLeft size={13} /> Reassign open tasks
                              </button>
                            )}
                            {!isSelf && (
                              <button
                                className={`table-edit-button ${member.active ? "table-danger-button" : ""}`}
                                data-testid={`${member.active ? "deactivate" : "reactivate"}-team-member-${member.id}`}
                                type="button"
                                onClick={() => setConfirm({ action: member.active ? "deactivate" : "reactivate", member })}
                              >
                                {member.active ? <UserMinus size={13} /> : <UserRoundCheck size={13} />}
                                {member.active ? " Deactivate" : " Reactivate"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!loaded && !visible.length && <p className="empty-requests list-loading" data-testid="team-loading">Loading team members…</p>}
              {loaded && !visible.length && (
                <p className="empty-requests" data-testid="team-empty-state">
                  No staff accounts match this search.
                </p>
              )}
            </div>
          </section>

          {editor && (
            <EditorModal onClose={() => setEditor(null)} testId="team-member-modal">
              <TeamMemberForm form={form} isSaving={isSaving} mode={editor.mode} onChange={setForm} onClose={() => setEditor(null)} onSubmit={submitForm} />
            </EditorModal>
          )}
          {credential && (
            <EditorModal onClose={() => setCredential(null)} testId="team-credential-modal">
              <TeamCredentialResult mode={credential.mode} result={credential} onClose={() => setCredential(null)} />
            </EditorModal>
          )}
          {confirm && (
            <EditorModal onClose={() => setConfirm(null)} testId="team-confirm-modal">
              <ConfirmPanel {...confirmCopy[confirm.action]} onCancel={() => setConfirm(null)} onConfirm={runConfirm} testId={`team-confirm-${confirm.action}`} />
            </EditorModal>
          )}
          {reassigning && (
            <EditorModal onClose={() => setReassigning(null)} testId="reassign-tasks-modal">
              <ReassignTasksPanel
                member={reassigning}
                onClose={() => setReassigning(null)}
                onDone={(result) => {
                  setReassigning(null);
                  setMessage(`${result.moved} open task${result.moved === 1 ? "" : "s"}${result.task_types?.length ? ` (${result.task_types.length} type${result.task_types.length === 1 ? "" : "s"})` : ""} moved from ${result.from_owner_name} to ${result.to_owner_name}; ${result.to_owner_name} received one digest (CEO copied).`);
                }}
              />
            </EditorModal>
          )}
        </main>
      </div>
    </div>
  );
}
